import { IQStreamMessageType, TIQStreamMessage } from '@cloud-wave/neon-common-lib';
import { match } from 'ts-pattern';

import { TStreamingState } from '../types/contextAndHookTypes';

const parseNdjsonLine = (line: string): TIQStreamMessage | null => {
  const trimmed = line.trim();

  if (!trimmed) {
    return null;
  }

  try {
    return JSON.parse(trimmed) as TIQStreamMessage;
  } catch {
    return null;
  }
};

type TReduceNdjsonLineParams = {
  state: TStreamingState;
  line: string;
  onUpdate: (state: TStreamingState) => void;
  isRethink: boolean;
};

const reduceNdjsonLine = ({
  state,
  line,
  onUpdate,
  isRethink
}: TReduceNdjsonLineParams): TStreamingState => {
  const msg = parseNdjsonLine(line);

  if (!msg) {
    return state;
  }

  return match(msg)
    .with({ type: IQStreamMessageType.rewrite }, ({ delta }) => {
      const next = { ...state, rewrite: (state.rewrite ?? '') + delta };
      onUpdate(next);
      return next;
    })
    .with({ type: IQStreamMessageType.feedback }, ({ delta }) => {
      const next = { ...state, feedback: [...(state.feedback ?? []), delta] };
      onUpdate(next);
      return next;
    })
    .with({ type: IQStreamMessageType.output }, ({ delta }) => {
      const next = { ...state, output: (state.output ?? '') + delta };
      onUpdate(next);
      return next;
    })
    .with({ type: IQStreamMessageType.reset }, () => {
      const next: TStreamingState = {};
      if (!isRethink) {
        onUpdate(next);
      }
      return next;
    })
    .with({ type: IQStreamMessageType.done }, () => state)
    .with({ type: IQStreamMessageType.error }, ({ message }) => {
      throw new Error(message);
    })
    .with({ type: IQStreamMessageType['keep-alive'] }, () => state)
    .exhaustive();
};

export const readNdjsonStream = async (
  body: ReadableStream<Uint8Array>,
  onUpdate: (state: TStreamingState) => void,
  isRethink = false,
): Promise<TStreamingState> => {
  const decoder = new TextDecoder();
  let buffer = '';
  let accumulator: TStreamingState = {};

  for await (const chunk of body) {
    const lines = (buffer + decoder.decode(chunk, { stream: true })).split('\n');
    buffer = lines.pop() ?? '';
    accumulator = lines.reduce((state, line) => reduceNdjsonLine({
      state,
      line,
      onUpdate,
      isRethink
    }), accumulator);
  }

  if (buffer.trim()) {
    accumulator = reduceNdjsonLine({
      state: accumulator,
      line: buffer,
      onUpdate,
      isRethink
    });
  }

  return accumulator;
};
